import { z } from "zod";
import { type BookListItemDto, bookListQuerySchema } from "./catalog";

/*
 * Reading-tracking contract shared by server and UI: status values, calendar-date rules,
 * schemas (issue messages are field error codes translated by the client) and DTOs.
 */

export const READING_STATUSES = [
  "none",
  "want_to_read",
  "reading",
  "finished",
  "abandoned",
] as const;
export type ReadingStatus = (typeof READING_STATUSES)[number];

export const READING_NOTE_MAX = 2_000;

/** The server's current UTC calendar date (`YYYY-MM-DD`). */
export function utcToday(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Latest accepted date: one day after the server's UTC date (time-zone tolerance). */
export function latestAllowedDate(now: Date = new Date()): string {
  return utcToday(new Date(now.getTime() + 24 * 60 * 60 * 1000));
}

export interface SessionDates {
  startedAt?: string | null;
  finishedAt?: string | null;
  abandonedAt?: string | null;
}

/**
 * Cross-field rules of a reading session, evaluated on the complete (merged) row. Returns field
 * error codes; an empty object means valid. `YYYY-MM-DD` strings compare lexicographically.
 */
export function validateSessionDates(
  dates: SessionDates,
  options: { now?: Date; requireAnyDate?: boolean } = {},
): Record<string, string> {
  const fields: Record<string, string> = {};
  const latest = latestAllowedDate(options.now);
  // Malformed values are reported by the field schemas; skip them here (refinements still run).
  const valid = (value: string | null | undefined) =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  const startedAt = valid(dates.startedAt);
  const finishedAt = valid(dates.finishedAt);
  const abandonedAt = valid(dates.abandonedAt);
  for (const [name, value] of Object.entries({ startedAt, finishedAt, abandonedAt })) {
    if (value && value > latest) fields[name] = "future_date";
  }
  if (finishedAt && abandonedAt) {
    fields.abandonedAt = "both_end_dates";
  } else if (options.requireAnyDate !== false && !startedAt && !finishedAt && !abandonedAt) {
    fields.startedAt = "date_required";
  }
  const end = finishedAt ?? abandonedAt;
  if (startedAt && end && end < startedAt) {
    const name = finishedAt ? "finishedAt" : "abandonedAt";
    fields[name] ??= "before_start";
  }
  return fields;
}

const calendarDate = z.iso.date({ error: "invalid_date" });

const progress = z
  .number({ error: "invalid" })
  .int({ error: "invalid" })
  .min(0, { error: "out_of_range" })
  .max(100, { error: "out_of_range" });

const note = z
  .string({ error: "invalid" })
  .max(READING_NOTE_MAX, { error: "too_long" })
  .transform((value) => (value.trim() === "" ? null : value));

function addFieldIssues(ctx: z.RefinementCtx, fields: Record<string, string>): void {
  for (const [name, code] of Object.entries(fields)) {
    ctx.addIssue({ code: "custom", message: code, path: [name] });
  }
}

/** `POST /api/books/:bookId/reading-sessions`: without `finishedAt` it starts, with it it records a read. */
export const readingSessionCreateSchema = z
  .object({
    startedAt: calendarDate.optional(),
    finishedAt: calendarDate.optional(),
    progressPercent: progress.optional(),
    note: note.nullish().transform((value) => value ?? null),
  })
  .superRefine((value, ctx) => {
    addFieldIssues(ctx, validateSessionDates(value, { requireAnyDate: false }));
  });
export type ReadingSessionCreateInput = z.input<typeof readingSessionCreateSchema>;

/** `PATCH /api/reading-sessions/:id`; `null` clears a field. Re-validated on the merged row by the server. */
export const readingSessionPatchSchema = z
  .object({
    startedAt: calendarDate.nullable().optional(),
    finishedAt: calendarDate.nullable().optional(),
    abandonedAt: calendarDate.nullable().optional(),
    progressPercent: progress.nullable().optional(),
    note: note.nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
      ctx.addIssue({ code: "custom", message: "required", path: ["startedAt"] });
      return;
    }
    // Only rules that can be decided from the body alone; the server checks the merged row.
    addFieldIssues(ctx, validateSessionDates(value, { requireAnyDate: false }));
  });
export type ReadingSessionPatchInput = z.input<typeof readingSessionPatchSchema>;

/** `POST /api/reading-sessions/:id/finish|abandon`. */
export const readingSessionEndSchema = z
  .object({ date: calendarDate.optional() })
  .superRefine((value, ctx) => {
    if (value.date && value.date > latestAllowedDate()) {
      ctx.addIssue({ code: "custom", message: "future_date", path: ["date"] });
    }
  });
export type ReadingSessionEndInput = z.input<typeof readingSessionEndSchema>;

/** Query of `GET /api/books`: the catalog query plus the "my status" filter. */
export const bookListWithStatusQuerySchema = bookListQuerySchema.extend({
  myStatus: z.enum(READING_STATUSES, { error: "invalid" }).optional(),
});
export type BookListWithStatusQuery = z.infer<typeof bookListWithStatusQuerySchema>;

// --- DTOs ----------------------------------------------------------------------

export interface ReadingSessionDto {
  id: string;
  bookId: string;
  startedAt: string | null;
  finishedAt: string | null;
  abandonedAt: string | null;
  progressPercent: number | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookReadingDto {
  status: ReadingStatus;
  wantToRead: { addedAt: string } | null;
  /** Newest first. */
  sessions: ReadingSessionDto[];
  /** Number of finished sessions ("read n times"). */
  finishedCount: number;
}

export type ReadingBookDto = Pick<
  BookListItemDto,
  "id" | "title" | "authors" | "hasCover" | "coverVersion"
>;

export interface ReadingSessionWithBookDto {
  session: ReadingSessionDto;
  book: ReadingBookDto;
}

export interface ReadingOverviewDto {
  reading: ReadingSessionWithBookDto[];
  wantToRead: { addedAt: string; book: ReadingBookDto }[];
  history: { year: number; sessions: ReadingSessionWithBookDto[] }[];
  stats: { year: number; finished: number }[];
}
